// The SOURCE strip of the "הוספת נכסים" sub-tab.
//
// The three discovery sources — הנכסים שלי / נכסי המשרד / מהשוק — used to render
// as three STACKED sections in one scroll, so the results the realtor actually
// wanted were routinely buried under two other sections (and, with the market
// segment usually the largest, below a long scroll). They are tabs now: one
// source visible at a time, each tab carrying its own count so the strip alone
// answers "where are the results?" without switching, and the first source that
// HAS results auto-selected the moment a result set lands.
//
// This file owns the strip + the selection policy only; the cards, the paid-tier
// blur and the empty states stay in AddListingsSubTab.
import { useEffect, useRef, useState } from "react";
import { Tab, Tabs, Stack, Typography } from "@material-ui/core";
import Label from "components/Label";
import type { MatchSegmentId } from "./matchesSearch";

export type AddListingsSource = {
  id: MatchSegmentId;
  /** Tab label. */
  label: string;
  /** Panel copy when THIS source is empty but another source has results. */
  emptyText: string;
};

/** Count + the text rendered on the tab ("12" — or "12+" for the capped market segment). */
export type AddListingsSourceCount = { count: number; text: string };

// Product priority order — and therefore the auto-select order: the realtor's
// own listings first, then the office's, then the open market.
export const ADD_LISTINGS_SOURCES: readonly AddListingsSource[] = [
  { id: "mine", label: "הנכסים שלי", emptyText: "אין נכסים מתאימים מבין הנכסים שלך" },
  { id: "office", label: "נכסי המשרד", emptyText: "אין נכסים מתאימים מבין נכסי המשרד" },
  { id: "market", label: "מהשוק", emptyText: "לא נמצאו נכסים מתאימים מהשוק" }
];

export function getAddListingsSource(id: MatchSegmentId): AddListingsSource {
  return ADD_LISTINGS_SOURCES.find((source) => source.id === id) ?? ADD_LISTINGS_SOURCES[0];
}

/**
 * Which source tab is open, plus the "jump to the first source that has results"
 * policy.
 *
 * `resultsKey` identifies the RESULT SET — pass the search hook's `filtersKey`
 * (filter chips + free-text query), which is written only when a search pass
 * SUCCEEDS. The auto-select therefore runs exactly ONCE PER RESULT SET: the first
 * time counts are known for a key we open the first non-empty source, record the
 * key, and never touch the selection again for that key. Consequences, all
 * deliberate:
 *
 *  - A manual tab click STICKS — including across the in-flight refresh of the
 *    same result set (a refresh keeps the same key), which is the case a bare
 *    "have I auto-selected yet" boolean would still get wrong on a stale-cache
 *    revalidation.
 *  - A NEW key (the realtor edited the query or a filter chip) is a new search
 *    whose hits may live in a different source, so it re-runs. Landing on an
 *    empty tab right after searching is exactly the confusion this replaces.
 *  - Remounting re-runs it: flipping to the minisite sub-tab and back unmounts
 *    this surface, the ref dies with it, and re-opening re-picks — the literal
 *    "when opening the results" case (results come back instantly from the
 *    session cache, so this fires on the first committed render).
 *
 * When every source is empty we settle on the first tab, and the caller renders
 * the global "no matches" state.
 */
export function useAddListingsSourceTab(
  counts: Record<MatchSegmentId, AddListingsSourceCount>,
  resultsKey: string,
  countsKnown: boolean
): { activeSource: MatchSegmentId; selectSource: (source: MatchSegmentId) => void } {
  const [activeSource, setActiveSource] = useState<MatchSegmentId>(ADD_LISTINGS_SOURCES[0].id);
  // The result set we have already auto-selected for (null = none yet).
  const autoSelectedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!countsKnown) return; // first pass still running — "0 results" isn't known yet
    if (autoSelectedKeyRef.current === resultsKey) return; // already decided for this set
    autoSelectedKeyRef.current = resultsKey;
    const firstWithHits = ADD_LISTINGS_SOURCES.find((source) => counts[source.id].count > 0);
    setActiveSource(firstWithHits?.id ?? ADD_LISTINGS_SOURCES[0].id);
  }, [countsKnown, resultsKey, counts]);

  return { activeSource, selectSource: setActiveSource };
}

export default function AddListingsSourceTabs({
  counts,
  value,
  onChange,
  showCounts
}: {
  counts: Record<MatchSegmentId, AddListingsSourceCount>;
  value: MatchSegmentId;
  onChange: (source: MatchSegmentId) => void;
  /** False until the first search pass lands — a "(0)" before any search ran is a lie. */
  showCounts: boolean;
}): JSX.Element {
  return (
    <Tabs
      value={value}
      onChange={(_, next) => onChange(next as MatchSegmentId)}
      // Scrollable (never wraps at narrow widths) but WITHOUT scroll buttons:
      // the v5-beta scroll-button math is broken under direction:rtl (see
      // RtlSafeTabScrollButton in ContactDetailTabs). Three short labels fit
      // everywhere; anything narrower still swipes.
      variant="scrollable"
      scrollButtons={false}
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        minHeight: 40,
        "& .MuiTab-root": { minHeight: 40, py: 1 }
      }}
    >
      {ADD_LISTINGS_SOURCES.map(({ id, label }) => {
        const { count, text } = counts[id];
        return (
          <Tab
            key={id}
            value={id}
            label={
              // Same label shape as the page-level strip: text + count chip.
              // An empty source keeps a muted "(0)" instead of the chip — the
              // count still has to be readable at a glance (that was the point
              // of the greyed "(0)" on the old stacked section headers).
              <Stack direction="row" alignItems="center" gap={0.75}>
                <span>{label}</span>
                {showCounts &&
                  (count > 0 ? (
                    <Label color={id === "market" ? "warning" : "info"}>{text}</Label>
                  ) : (
                    <Typography component="span" variant="caption" color="text.disabled">
                      (0)
                    </Typography>
                  ))}
              </Stack>
            }
          />
        );
      })}
    </Tabs>
  );
}
